import type { ArtifactProto, ExecutionStepProto } from "build-ts/chat_session";
import { ArtifactType, artifactTypeFromJSON } from "build-ts/common";
import { DarwinToolType } from "build-ts/darwin_agent";
import { findToolType, type ToolExecutionRun } from "./executionStepToolRunGroups";

/** Artifacts in step order (streaming accumulation within the run). */
function collectArtifactsInOrder(steps: ExecutionStepProto[]): ArtifactProto[] {
	const out: ArtifactProto[] = [];
	for (const s of steps) {
		for (const a of s.artifacts ?? []) {
			out.push(a);
		}
	}
	return out;
}

/**
 * Artifacts that can be opened in the side panel for a **successful** tool run.
 * Skills do not expose a loadable artifact. Multiple `ANALYSIS_OUTPUT` (search) are all returned.
 */
export function resolveArtifactsForSuccessfulToolRun(run: ToolExecutionRun): ArtifactProto[] {
	if (!run.completed || run.failed) return [];

	const tool = findToolType(run.steps);
	if (tool === DarwinToolType.SKILL_TOOL) return [];

	const ordered = collectArtifactsInOrder(run.steps);
	if (!ordered.length) return [];

	const analyses = ordered.filter(a => artifactTypeFromJSON(a.artifact_type) === ArtifactType.ANALYSIS_OUTPUT);
	if (analyses.length > 0) return analyses;

	const dataframes = ordered.filter(a => artifactTypeFromJSON(a.artifact_type) === ArtifactType.KG_DATAFRAME);
	if (dataframes.length > 0) return [dataframes[dataframes.length - 1]!];

	const dataframeAnalyses = ordered.filter(a => artifactTypeFromJSON(a.artifact_type) === ArtifactType.DATAFRAME_ANALYSIS);
	if (dataframeAnalyses.length > 0) return [dataframeAnalyses[dataframeAnalyses.length - 1]!];

	const observations = ordered.filter(a => artifactTypeFromJSON(a.artifact_type) === ArtifactType.KG_OBSERVATION);
	if (observations.length > 0) return [observations[observations.length - 1]!];

	const jobs = ordered.filter(a => artifactTypeFromJSON(a.artifact_type) === ArtifactType.SCENARIO_JOB);
	if (jobs.length > 0) return [jobs[jobs.length - 1]!];

	const scenarios = ordered.filter(a => artifactTypeFromJSON(a.artifact_type) === ArtifactType.SCENARIO);
	if (scenarios.length > 0) return [scenarios[scenarios.length - 1]!];

	return [];
}
